import React, { useState, useEffect } from 'react';
import SEO from '../components/SEO';
import PageActions from '../components/PageActions';

const RentPage = () => {
  useEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, []);

  const CONTRACTUAL_DAYS = 46;
  const YEARLY_PITCH_FEE = 8064;
  const MONTHLY_PITCH_FEE = 672;

  const [inputs, setInputs] = useState({
    contractualDays: 46,
    contractualPrice: 198,
    friendsDays: 0,
    friendsPrice: 0,
    familyDays: 0,
    familyPrice: 0,
  });

  const handleChange = (field, value) => {
    const num = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
    setInputs(prev => {
      const next = { ...prev, [field]: num };
      // Cap contractual days at 46
      if (next.contractualDays > CONTRACTUAL_DAYS) next.contractualDays = CONTRACTUAL_DAYS;
      // Cap total days at 365
      const total = next.contractualDays + next.friendsDays + next.familyDays;
      if (total > 365) {
        next[field] = Math.max(0, num - (total - 365));
      }
      return next;
    });
  };

  const contractualTurnover = inputs.contractualDays * inputs.contractualPrice;
  const friendsTurnover = inputs.friendsDays * inputs.friendsPrice;
  const familyTurnover = inputs.familyDays * inputs.familyPrice;
  const totalDays = inputs.contractualDays + inputs.friendsDays + inputs.familyDays;
  const yearlyRentalIncome = contractualTurnover + friendsTurnover + familyTurnover;
  const adaptedPitchFee = YEARLY_PITCH_FEE - yearlyRentalIncome;

  const formatCurrency = (val) => {
    const formatted = Math.abs(val).toLocaleString('en-GB');
    return val < 0 ? `- £${formatted}` : `£${formatted}`;
  };

  return (
    <div className="bg-[#f5f1e8]">
      <SEO
        title="Pitch Fee - Rental Income Calculator | Your Setl"
        description="See how renting out your country cabin can cover your annual pitch fee. Try the calculator with your own numbers."
        keywords="rent cabin Dorset, country cabin rental income, pitch fee, holiday let Dorset, cabin investment UK"
        url="https://www.yoursetl.co.uk/pitch-fee"
      />
      <PageActions />

      {/* Hero */}
      <section className="relative h-[60vh] md:h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(https://res.cloudinary.com/dvypx0zh5/image/upload/f_auto,q_auto:best/v1771402943/DSC_8350_dmznve.jpg)` }}>
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 px-8 md:px-16 pb-16 md:pb-20 max-w-3xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light text-[#f5f1e8] leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}>
            Pitch Fee
          </h1>
          <div className="w-16 h-px bg-[#f5f1e8]/70 my-5" />
          <p className="text-base md:text-lg font-light text-[#f5f1e8] tracking-wide"
            style={{ fontFamily: 'Inter, sans-serif' }}>
            Earn from your cabin when you're not there
          </p>
        </div>
      </section>

      {/* Pitch Fee Info */}
      <section className="py-16 lg:py-20 px-6 bg-[#f5f1e8]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-sm uppercase tracking-widest text-[#d4a574] mb-3"
                style={{ fontFamily: 'Inter, sans-serif' }}>
                Pitch fee
              </p>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-5xl md:text-6xl font-light text-[#2a3a2a]"
                  style={{ fontFamily: 'Playfair Display, serif' }}>
                  £{MONTHLY_PITCH_FEE}
                </span>
                <span className="text-lg text-[#3a2f28]/70" style={{ fontFamily: 'Inter, sans-serif' }}>
                  per month <span className="text-sm">(excl. VAT)</span>
                </span>
              </div>
              <p className="text-sm text-[#3a2f28]/60 mb-8 italic" style={{ fontFamily: 'Inter, sans-serif' }}>
                (Reviewed annually in line with indexation)
              </p>

              <p className="text-base font-semibold text-[#3a2f28] mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                This covers:
              </p>
              <ul className="space-y-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                {[
                  'Use of the land',
                  'Maintenance of shared areas and landscape',
                  'Water and waste systems (including septic maintenance)',
                  'Site access and infrastructure',
                  'General upkeep of the setting',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-[#3a2f28]/80">
                    <span className="text-[#d4a574] mt-0.5">—</span>
                    <span className="text-base">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 lg:py-20 px-6 bg-[#e8e2d8]" data-testid="rent-calculator">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-light text-[#3a2f28] mb-3 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}>
            Rental Income Calculator
          </h2>
          <p className="text-base text-[#3a2f28]/70 mb-10 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            Fill in your days and price per night to calculate your rental income
          </p>

          {/* Calculator Table */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 bg-[#3a2f28] text-[#f5f1e8] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 font-medium"></div>
              <div className="col-span-2 px-3 py-3 font-medium text-center">Days</div>
              <div className="col-span-2 px-3 py-3 font-medium text-center bg-[#2a3a2a]">Your days</div>
              <div className="col-span-2 px-3 py-3 font-medium text-center bg-[#2a3a2a]">£ / night</div>
              <div className="col-span-2 px-3 py-3 font-medium text-right">Turnover</div>
            </div>

            {/* Contractual limited dates */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-4 text-sm text-[#3a2f28]">
                Contractual limited dates <span className="text-[#d4a574]">*</span>
              </div>
              <div className="col-span-2 px-3 py-4 text-center text-sm text-[#3a2f28]/60">{CONTRACTUAL_DAYS}</div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-contractual-days"
                  type="number"
                  min="0"
                  max={CONTRACTUAL_DAYS}
                  value={inputs.contractualDays || ''}
                  onChange={(e) => handleChange('contractualDays', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-contractual-price"
                  type="number"
                  min="0"
                  value={inputs.contractualPrice || ''}
                  onChange={(e) => handleChange('contractualPrice', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-4 py-4 text-right text-sm font-medium text-[#3a2f28]">
                {formatCurrency(contractualTurnover)}
              </div>
            </div>

            {/* Friends */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-4 text-sm text-[#3a2f28]">Friends</div>
              <div className="col-span-2 px-3 py-4 text-center text-sm text-[#3a2f28]/60"></div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-friends-days"
                  type="number"
                  min="0"
                  value={inputs.friendsDays || ''}
                  onChange={(e) => handleChange('friendsDays', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-friends-price"
                  type="number"
                  min="0"
                  value={inputs.friendsPrice || ''}
                  onChange={(e) => handleChange('friendsPrice', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-4 py-4 text-right text-sm font-medium text-[#3a2f28]">
                {formatCurrency(friendsTurnover)}
              </div>
            </div>

            {/* Family */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-4 text-sm text-[#3a2f28]">Family</div>
              <div className="col-span-2 px-3 py-4 text-center text-sm text-[#3a2f28]/60"></div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-family-days"
                  type="number"
                  min="0"
                  value={inputs.familyDays || ''}
                  onChange={(e) => handleChange('familyDays', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-3 py-2">
                <input
                  data-testid="input-family-price"
                  type="number"
                  min="0"
                  value={inputs.familyPrice || ''}
                  onChange={(e) => handleChange('familyPrice', e.target.value)}
                  className="w-full text-center py-2 bg-[#f0f7f0] border border-[#c5dcc5] rounded text-sm text-[#3a2f28] focus:outline-none focus:ring-2 focus:ring-[#d4a574]"
                />
              </div>
              <div className="col-span-2 px-4 py-4 text-right text-sm font-medium text-[#3a2f28]">
                {formatCurrency(familyTurnover)}
              </div>
            </div>

            {/* Spacer */}
            <div className="h-2 bg-[#f5f1e8]"></div>

            {/* Summary rows */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8] bg-[#fafaf5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Yearly Rental income</div>
              <div className="col-span-8 px-4 py-3 text-right text-sm font-medium text-[#3a2f28]">
                {formatCurrency(yearlyRentalIncome)}
              </div>
            </div>

            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8] bg-[#fafaf5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Yearly Pitch fee</div>
              <div className="col-span-6"></div>
              <div className="col-span-2 px-4 py-3 text-right text-sm text-[#3a2f28]/70">
                {formatCurrency(YEARLY_PITCH_FEE)}
              </div>
            </div>

            {/* Result */}
            <div className={`grid grid-cols-12 items-center ${yearlyRentalIncome >= YEARLY_PITCH_FEE ? 'bg-[#f0f7f0]' : 'bg-[#faf5f0]'}`} style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-4 text-base font-semibold text-[#3a2f28]">
                {yearlyRentalIncome >= YEARLY_PITCH_FEE ? 'Your surplus/year' : 'Remaining pitch fee/year'}
              </div>
              <div className="col-span-6"></div>
              <div className={`col-span-2 px-4 py-4 text-right text-lg font-bold ${yearlyRentalIncome >= YEARLY_PITCH_FEE ? 'text-[#2a3a2a]' : 'text-[#3a2f28]'}`}>
                {formatCurrency(Math.abs(adaptedPitchFee))}
              </div>
            </div>
          </div>

          {/* Footnote */}
          <div className="mt-6 space-y-1" style={{ fontFamily: 'Inter, sans-serif' }}>
            <p className="text-sm text-[#3a2f28]/60">
              <span className="text-[#d4a574] font-semibold">*</span> Contractual limited dates
            </p>
            <p className="text-sm text-[#3a2f28]/60 ml-3">
              — Are the same for the 8 cabin owners
            </p>
            <p className="text-sm text-[#3a2f28]/60 ml-3">
              — Protect the owner against changing population
            </p>
            <p className="text-sm text-[#3a2f28]/60 ml-3">
              — You can use distributors to rent out your cabin
            </p>
          </div>
        </div>
      </section>

      {/* Fixed Example */}
      <section className="py-16 lg:py-20 px-6 bg-[#f5f1e8]" data-testid="rent-example">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl md:text-3xl font-light text-[#3a2f28] mb-2 text-center"
            style={{ fontFamily: 'Playfair Display, serif' }}>
            Example
          </h3>
          <p className="text-base text-[#3a2f28]/70 mb-8 text-center" style={{ fontFamily: 'Inter, sans-serif' }}>
            Renting to guests, friends and family can fully offset your pitch fee
          </p>

          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-12 bg-[#3a2f28] text-[#f5f1e8] text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 font-medium"></div>
              <div className="col-span-2 px-3 py-3 font-medium text-center">Days</div>
              <div className="col-span-2 px-3 py-3 font-medium text-center">Days rented</div>
              <div className="col-span-2 px-3 py-3 font-medium text-center">£ / night</div>
              <div className="col-span-2 px-3 py-3 font-medium text-right">Turnover</div>
            </div>

            {/* Contractual */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Contractual limited dates <span className="text-[#d4a574]">*</span></div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]/60">46</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">40</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">198</div>
              <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-[#3a2f28]">£7,920</div>
            </div>

            {/* Friends */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Friends</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]/60">319</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">15</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">80</div>
              <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-[#3a2f28]">£1,200</div>
            </div>

            {/* Family */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Family</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]/60"></div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">15</div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]">40</div>
              <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-[#3a2f28]">£600</div>
            </div>

            {/* Spacer */}
            <div className="h-2 bg-[#f5f1e8]"></div>

            {/* Summary rows */}
            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8] bg-[#fafaf5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Yearly Rental income</div>
              <div className="col-span-2"></div>
              <div className="col-span-2 px-3 py-3 text-center text-sm text-[#3a2f28]/60">70</div>
              <div className="col-span-2"></div>
              <div className="col-span-2 px-4 py-3 text-right text-sm font-medium text-[#3a2f28]">£9,720</div>
            </div>

            <div className="grid grid-cols-12 items-center border-b border-[#e8e2d8] bg-[#fafaf5]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-3 text-sm text-[#3a2f28]">Yearly Pitch fee</div>
              <div className="col-span-8 px-4 py-3 text-right text-sm text-[#3a2f28]/70">£8,064</div>
            </div>

            {/* Result - green because income exceeds fee */}
            <div className="grid grid-cols-12 items-center bg-[#f0f7f0]" style={{ fontFamily: 'Inter, sans-serif' }}>
              <div className="col-span-4 px-4 py-4 text-base font-semibold text-[#3a2f28]">Your surplus/year</div>
              <div className="col-span-8 px-4 py-4 text-right text-lg font-bold text-[#2a3a2a]">£1,656</div>
            </div>
          </div>

          <p className="text-sm text-[#3a2f28]/60 mt-6 text-center italic" style={{ fontFamily: 'Inter, sans-serif' }}>
            In this example, rental income covers the annual pitch fee. Your cabin pays for itself.
          </p>
        </div>
      </section>
    </div>
  );
};

export default RentPage;
